// ✅ src/components/screens/PublicStoreScreen.js (FULL)
// ✅ LOGIC KEPT: store load, subscription gate, wishlist, cart add-once, options popup, discount apply
// ✅ CHANGES YOU ASKED:
//   1) ✅ MOBILE: Category + Filters moved into HAMBURGER (drawer)
//   2) ✅ REMOVED "Express delivery"
//   3) ✅ PRICE FILTER IS CLICKABLE + WORKS (min/max + Apply + Clear)
//   4) ✅ FIX: Removed the hamburger button beside the logo (kept the full "Categories & Filters" button)

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../../lib/firebase";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

function safe(v) {
  return String(v ?? "").trim();
}

function money(n, cur = "GHS") {
  const num = Number(n);
  if (Number.isNaN(num)) return `${cur} —`;
  return `${cur} ${num.toLocaleString()}`;
}

function initials(name) {
  const s = safe(name);
  if (!s) return "U";
  const parts = s.split(" ").filter(Boolean);
  const a = parts[0]?.[0] || "U";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase();
}

function iconForCategory(label) {
  const v = safe(label).toLowerCase();
  if (!v || v === "all") return "🧺";
  if (v.includes("elect")) return "🖥️";
  if (v.includes("phone")) return "📱";
  if (v.includes("fashion") || v.includes("cloth")) return "👕";
  if (v.includes("home") || v.includes("house")) return "🏡";
  if (v.includes("toy")) return "🧸";
  if (v.includes("beauty")) return "💄";
  if (v.includes("shoe")) return "👟";
  if (v.includes("watch")) return "⌚";
  return "🛍️";
}

function getPriceValue(p) {
  const candidates = [
    p?.price,
    p?.amount,
    p?.unitPrice,
    p?.priceGHS,
    p?.priceValue,
    p?.salePrice,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  const zero = Number(p?.price);
  if (!Number.isNaN(zero)) return zero;
  return null;
}

/* ---------------- wishlist ---------------- */
const WISHLIST_KEY = "wishlist_items_v1";

function loadWishlistItems() {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveWishlistItems(items) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items || []));
  } catch {}
}

function wishlistMapFromItems(items) {
  const map = {};
  (items || []).forEach((it) => {
    if (it?.id) map[String(it.id)] = true;
  });
  return map;
}

/* ---------------- cart (PER STORE) ---------------- */
function cartKey(storeId) {
  return `cart_items_v1:${String(storeId || "unknown")}`;
}

function loadCart(storeId) {
  try {
    const raw = localStorage.getItem(cartKey(storeId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveCart(storeId, items) {
  try {
    localStorage.setItem(cartKey(storeId), JSON.stringify(items || []));
  } catch {}
}

function cartCount(items) {
  try {
    return (items || []).reduce(
      (sum, it) => sum + Math.max(0, Number(it?.qty || 0)),
      0
    );
  } catch {
    return 0;
  }
}

/* ---------------- click guard ---------------- */
function stopAll(e) {
  try {
    e?.preventDefault?.();
    e?.stopPropagation?.();
  } catch {}
}

/* ---------------- subscription helpers ---------------- */
function toMs(ts) {
  try {
    if (!ts) return 0;
    if (ts?.toDate) return ts.toDate().getTime();
    if (typeof ts === "number") return ts;
    if (ts?.seconds != null) return ts.seconds * 1000;
    if (typeof ts === "string") {
      const d = new Date(ts);
      const ms = d.getTime();
      return Number.isFinite(ms) ? ms : 0;
    }
    return 0;
  } catch {
    return 0;
  }
}

function isSubActive(subData) {
  const status = safe(subData?.status).toUpperCase();
  const endMs = toMs(subData?.currentPeriodEnd);
  const now = Date.now();
  return status === "ACTIVE" && endMs > now;
}

/* ---------------- product option helpers ---------------- */
function normalizeList(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.map((x) => safe(x)).filter(Boolean);
  if (typeof v === "string") {
    return v
      .split(/[,|/\n]+/g)
      .map((x) => safe(x))
      .filter(Boolean);
  }
  return [];
}

function expandRangeToken(token) {
  const t = safe(token);
  const m = t.match(/^(\d+)\s*-\s*(\d+)$/);
  if (!m) return null;

  const a = Number(m[1]);
  const b = Number(m[2]);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;

  const start = Math.min(a, b);
  const end = Math.max(a, b);

  const maxCount = 200;
  if (end - start + 1 > maxCount) return [t];

  const out = [];
  for (let i = start; i <= end; i++) out.push(String(i));
  return out;
}

function getAttrOptions(p, keys = []) {
  const attrs = p?.attributes;
  if (!attrs || typeof attrs !== "object") return [];

  const map = new Map(
    Object.entries(attrs).map(([k, v]) => [safe(k).toLowerCase(), v])
  );

  for (const k of keys) {
    const raw = map.get(safe(k).toLowerCase());
    const s = safe(raw);
    if (!s) continue;

    const parts = s
      .split(/[,|/\n]+/g)
      .map((x) => safe(x))
      .filter(Boolean);

    const out = [];
    for (const part of parts) {
      const expanded = expandRangeToken(part);
      if (expanded) out.push(...expanded);
      else out.push(part);
    }

    const seen = new Set();
    const final = [];
    for (const item of out) {
      const key = safe(item).toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      final.push(item);
    }
    return final;
  }
  return [];
}

function getSizes(p) {
  const out = [
    ...normalizeList(p?.sizes),
    ...normalizeList(p?.size),
    ...normalizeList(p?.sizeOptions),
    ...normalizeList(p?.availableSizes),
    ...normalizeList(p?.variants?.sizes),
    ...normalizeList(p?.variants?.size),
    ...normalizeList(p?.options?.sizes),
    ...normalizeList(p?.options?.size),
    ...getAttrOptions(p, ["size", "sizes"]),
  ];

  const seen = new Set();
  const final = [];
  for (const item of out) {
    const k = safe(item).toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    final.push(item);
  }
  return final;
}

function getColors(p) {
  const out = [
    ...normalizeList(p?.colors),
    ...normalizeList(p?.color),
    ...normalizeList(p?.colorOptions),
    ...normalizeList(p?.availableColors),
    ...normalizeList(p?.variants?.colors),
    ...normalizeList(p?.variants?.color),
    ...normalizeList(p?.options?.colors),
    ...normalizeList(p?.options?.color),
    ...getAttrOptions(p, ["color", "colors", "colour", "colours"]),
  ];

  const seen = new Set();
  const final = [];
  for (const item of out) {
    const k = safe(item).toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    final.push(item);
  }
  return final;
}

function hasOptions(p) {
  const sizes = getSizes(p);
  const colors = getColors(p);
  return sizes.length > 0 || colors.length > 0;
}

/* ---------------- discount helpers ---------------- */
function applyDiscount(priceVal, discountPercent) {
  const p = Number(priceVal);
  const d = Number(discountPercent || 0);
  if (!Number.isFinite(p)) return null;
  if (!Number.isFinite(d) || d <= 0) return p;

  const clamped = Math.max(0, Math.min(100, d));
  const raw = p * (1 - clamped / 100);
  const final = Math.round(raw);
  return Number.isFinite(final) ? final : p;
}

export default function PublicStoreScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { storeSlug: rawStoreSlug } = useParams();
  const storeSlug = useMemo(() => safe(rawStoreSlug), [rawStoreSlug]);

  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("All");

  const [subBlocked, setSubBlocked] = useState(false);

  const [wishlist, setWishlist] = useState(() =>
    wishlistMapFromItems(loadWishlistItems())
  );

  const [cart, setCart] = useState([]);
  const [user, setUser] = useState(() => auth.currentUser);

  // ✅ options popup state
  const [optOpen, setOptOpen] = useState(false);
  const [optProduct, setOptProduct] = useState(null);
  const [optSize, setOptSize] = useState("");
  const [optColor, setOptColor] = useState("");

  // ✅ UI: sort
  const [sortBy, setSortBy] = useState("Newest"); // Newest | PriceLow | PriceHigh | Discount

  // ✅ UI: price filter (clickable + works)
  const [priceMinInput, setPriceMinInput] = useState("");
  const [priceMaxInput, setPriceMaxInput] = useState("");
  const [priceMinApplied, setPriceMinApplied] = useState(null);
  const [priceMaxApplied, setPriceMaxApplied] = useState(null);

  // ✅ MOBILE hamburger drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  const dbg = () => {};

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      dbg("Auth state changed", { uid: u?.uid || null });
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!store?.id) return;
    saveCart(store.id, cart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, store?.id]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e?.key === WISHLIST_KEY) {
        const items = loadWishlistItems();
        setWishlist(wishlistMapFromItems(items));
      }
      if (store?.id && e?.key === cartKey(store.id)) {
        const fresh = loadCart(store.id);
        setCart(fresh);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  useEffect(() => {
    if (!store?.id) return;
    const sync = () => setCart(loadCart(store.id));
    window.addEventListener("focus", sync);
    return () => window.removeEventListener("focus", sync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const goSignup = (e) => {
    stopAll(e);
    const nextPath = location?.pathname || `/s/${storeSlug}`;
    navigate("/login", { state: { next: nextPath } });
  };

  const goProfile = (e) => {
    stopAll(e);
    if (store?.id) return navigate(`/me?storeId=${encodeURIComponent(store.id)}`);
    navigate("/me");
  };

  const goCart = (e) => {
    stopAll(e);
    if (!store?.id) return navigate("/cart");
    navigate(`/cart?store=${encodeURIComponent(store.id)}`);
  };

  const isInCart = (productId) => {
    const pid = String(productId);
    return (cart || []).some(
      (x) => String(x?.productId) === pid && Number(x?.qty || 0) > 0
    );
  };

  const addToCartFinal = (productId, options = {}) => {
    const pid = String(productId);
    const sid = store?.id;
    if (!pid || !sid) return;

    setCart((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const idx = list.findIndex((x) => String(x?.productId) === pid);

      if (idx >= 0 && Number(list[idx]?.qty || 0) > 0) return list;

      const line = {
        productId: pid,
        qty: 1,
        size: safe(options?.size),
        color: safe(options?.color),
      };

      list.push(line);
      saveCart(sid, list);
      return list;
    });
  };

  const openOptionsPopup = (p) => {
    const sizes = getSizes(p);
    const colors = getColors(p);

    setOptProduct(p || null);
    setOptSize(sizes[0] || "");
    setOptColor(colors[0] || "");
    setOptOpen(true);
  };

  const closeOptionsPopup = () => {
    setOptOpen(false);
    setOptProduct(null);
    setOptSize("");
    setOptColor("");
  };

  const addToCart = (e, productId, p) => {
    stopAll(e);
    if (subBlocked) return;
    if (isInCart(productId)) return;

    if (p && hasOptions(p)) {
      openOptionsPopup(p);
      return;
    }

    addToCartFinal(productId, {});
  };

  const toggleWish = (e, p) => {
    stopAll(e);
    const pid = String(p?.id);
    if (!pid) return;

    const items = loadWishlistItems();
    const list = Array.isArray(items) ? [...items] : [];
    const idx = list.findIndex((x) => String(x?.id) === pid);

    if (idx >= 0) {
      list.splice(idx, 1);
    } else {
      const priceVal = getPriceValue(p);
      list.push({
        id: pid,
        title: safe(p?.title) || "Item",
        price: priceVal == null ? 0 : Number(priceVal),
        currency: safe(p?.currency) || "GHS",
        imageUrl:
          safe(p?.thumbnailUrl) ||
          safe(p?.imageUrl) ||
          safe(p?.mediaUrls?.[0]) ||
          "",
        storeId: safe(store?.id),
        storeName: safe(store?.storeName),
        createdAt: Date.now(),
      });
    }

    saveWishlistItems(list);
    setWishlist(wishlistMapFromItems(list));
  };

  const goView = (e, productId) => {
    stopAll(e);
    if (subBlocked) return;
    const pid = safe(productId);
    if (!pid) return;
    navigate(`/p/${encodeURIComponent(pid)}`);
  };

  const goCard = (productId) => {
    if (subBlocked) return;
    const pid = safe(productId);
    if (!pid) return;
    navigate(`/p/${encodeURIComponent(pid)}`);
  };

  // ✅ apply price filter
  const applyPrice = () => {
    const min = Number(String(priceMinInput || "").replace(/[^\d.]/g, ""));
    const max = Number(String(priceMaxInput || "").replace(/[^\d.]/g, ""));

    const minOk = Number.isFinite(min) && min > 0;
    const maxOk = Number.isFinite(max) && max > 0;

    setPriceMinApplied(minOk ? min : null);
    setPriceMaxApplied(maxOk ? max : null);

    // on mobile, close drawer after apply
    setDrawerOpen(false);
  };

  const clearPrice = () => {
    setPriceMinInput("");
    setPriceMaxInput("");
    setPriceMinApplied(null);
    setPriceMaxApplied(null);
  };

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setMsg("");
      setStore(null);
      setProducts([]);
      setCart([]);
      setSubBlocked(false);

      if (!storeSlug) {
        setMsg("❌ URL slug missing. Use /s/your-slug");
        setLoading(false);
        return;
      }

      try {
        let storeDocSnap = null;

        try {
          const storeQ = query(
            collection(db, "stores"),
            where("storeSlug", "==", storeSlug),
            limit(1)
          );
          const storeSnap = await getDocs(storeQ);
          if (!storeSnap.empty) storeDocSnap = storeSnap.docs[0];
        } catch {}

        if (!storeDocSnap) {
          const byIdSnap = await getDoc(doc(db, "stores", storeSlug));
          if (byIdSnap.exists()) {
            storeDocSnap = { id: byIdSnap.id, data: () => byIdSnap.data() };
          }
        }

        if (!storeDocSnap) {
          setMsg(
            "❌ No store found for this link. (Tip: set stores/{uid}.storeSlug OR use /s/{uid})"
          );
          setLoading(false);
          return;
        }

        const storeId = storeDocSnap.id;
        const storeData = { id: storeId, ...(storeDocSnap.data() || {}) };

        if (!alive) return;
        setStore(storeData);
        setCart(loadCart(storeId));

        let active = false;

        try {
          const subSnap = await getDoc(doc(db, "subscriptions", storeId));
          const subData = subSnap.exists() ? subSnap.data() : null;
          if (subData) active = isSubActive(subData);
        } catch {}

        if (!active) {
          const sStatus = safe(storeData?.subscriptionStatus).toUpperCase();
          const endMs = toMs(storeData?.subscriptionExpiresAt);
          active = sStatus === "ACTIVE" && endMs > Date.now();
        }

        if (!alive) return;

        if (!active) {
          setSubBlocked(true);
          setProducts([]);
          setMsg("⚠️ This store is temporarily inactive (seller not subscribed).");
          setLoading(false);
          return;
        }

        setSubBlocked(false);

        let list = [];

        try {
          const activeByStatusQ = query(
            collection(db, "products"),
            where("storeId", "==", storeId),
            where("status", "==", "ACTIVE")
          );
          const prodSnap = await getDocs(activeByStatusQ);
          list = prodSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        } catch {}

        if (list.length === 0) {
          try {
            const activeByBoolQ = query(
              collection(db, "products"),
              where("storeId", "==", storeId),
              where("isActive", "==", true)
            );
            const prodSnap2 = await getDocs(activeByBoolQ);
            list = prodSnap2.docs.map((d) => ({
              id: d.id,
              ...(d.data() || {}),
            }));
          } catch {}
        }

        list.sort((a, b) => {
          const am = a?.updatedAt?.seconds ? a.updatedAt.seconds : 0;
          const bm = b?.updatedAt?.seconds ? b.updatedAt.seconds : 0;
          return bm - am;
        });

        if (!alive) return;
        setProducts(list);
        setLoading(false);
      } catch (e) {
        const text = e?.message || "Failed to load store.";
        setMsg(text);
        setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSlug]);

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      const cn = safe(p.categoryName);
      const cid = safe(p.categoryId);
      if (cn) set.add(cn);
      else if (cid) set.add(cid);
    });
    return ["All", ...Array.from(set)];
  }, [products]);

  const filtered = useMemo(() => {
    const s = safe(search).toLowerCase();

    return products.filter((p) => {
      const title = safe(p.title).toLowerCase();
      const desc = safe(p.description).toLowerCase();
      const type = safe(p.productType).toLowerCase();
      const catAny = (safe(p.categoryName) || safe(p.categoryId)).toLowerCase();

      const matchesSearch =
        !s ||
        title.includes(s) ||
        desc.includes(s) ||
        type.includes(s) ||
        catAny.includes(s);

      const pCatName = safe(p.categoryName);
      const pCatId = safe(p.categoryId);
      const matchesCat =
        activeCat === "All" ||
        pCatName === activeCat ||
        pCatId === activeCat ||
        (pCatName || pCatId) === activeCat;

      // ✅ price filter uses FINAL price (after discount)
      const pv = getPriceValue(p);
      const disc = Number(p?.discountPercent || 0);
      const finalPrice = applyDiscount(pv, disc);
      const fp = Number(finalPrice ?? pv ?? 0);

      const minOk = priceMinApplied == null || fp >= Number(priceMinApplied);
      const maxOk = priceMaxApplied == null || fp <= Number(priceMaxApplied);

      return matchesSearch && matchesCat && minOk && maxOk;
    });
  }, [products, search, activeCat, priceMinApplied, priceMaxApplied]);

  const sorted = useMemo(() => {
    const list = Array.isArray(filtered) ? [...filtered] : [];

    const getFinal = (p) => {
      const pv = getPriceValue(p);
      const d = Number(p?.discountPercent || 0);
      const fp = applyDiscount(pv, d);
      return Number(fp ?? pv ?? 0);
    };

    if (sortBy === "PriceLow") list.sort((a, b) => getFinal(a) - getFinal(b));
    if (sortBy === "PriceHigh") list.sort((a, b) => getFinal(b) - getFinal(a));
    if (sortBy === "Discount") {
      list.sort(
        (a, b) => Number(b?.discountPercent || 0) - Number(a?.discountPercent || 0)
      );
    }
    return list;
  }, [filtered, sortBy]);

  const bannerBg = useMemo(() => {
    const banner = safe(store?.bannerUrl);
    if (banner) return `url("${banner}")`;
    return "linear-gradient(135deg, #ff7a18, #ffb000)";
  }, [store]);

  const storeName = safe(store?.storeName) || "Store";
  const storeBio = safe(store?.bio) || "Shop great deals";
  const profileText = useMemo(() => {
    const name = safe(user?.displayName);
    if (name) return initials(name);
    const em = safe(user?.email);
    if (em) return initials(em.split("@")[0]);
    return "ME";
  }, [user]);

  const cartBadge = useMemo(() => cartCount(cart), [cart]);

  const popSizes = useMemo(() => getSizes(optProduct), [optProduct]);
  const popColors = useMemo(() => getColors(optProduct), [optProduct]);
  const popNeedsSize = popSizes.length > 0;
  const popNeedsColor = popColors.length > 0;

  const priceActiveText = useMemo(() => {
    const a = priceMinApplied != null ? `min ${Number(priceMinApplied)}` : "";
    const b = priceMaxApplied != null ? `max ${Number(priceMaxApplied)}` : "";
    const both = [a, b].filter(Boolean).join(" · ");
    return both ? `Price: ${both}` : "";
  }, [priceMinApplied, priceMaxApplied]);

  const closeDrawer = () => setDrawerOpen(false);

  const DrawerContent = (
    <div className="drawerPanel" onClick={(e) => stopAll(e)} role="dialog" aria-modal="true">
      <div className="drawerHead">
        <div className="drawerTitle">Categories & Filters</div>
        <button type="button" className="drawerX" onClick={closeDrawer}>
          ✕
        </button>
      </div>

      <div className="drawerBody">
        <div className="sideCard">
          <div className="sideTitle">CATEGORY</div>
          <div className="sideList">
            {categories.slice(0, 24).map((c) => (
              <button
                key={c}
                type="button"
                className={`sideItem ${activeCat === c ? "on" : ""}`}
                disabled={subBlocked}
                onClick={(e) => {
                  stopAll(e);
                  if (subBlocked) return;
                  setActiveCat(c);
                  setDrawerOpen(false);
                }}
                title={c}
              >
                <span className="sideIcon">{iconForCategory(c)}</span>
                <span className="sideLabel">{c}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="sideCard">
          <div className="sideTitle">PRICE (GHS)</div>

          <div className="rangeRow">
            <input
              className="rangeIn"
              value={priceMinInput}
              onChange={(e) => setPriceMinInput(e.target.value)}
              placeholder="min"
              inputMode="numeric"
              disabled={subBlocked}
            />
            <span className="dash">—</span>
            <input
              className="rangeIn"
              value={priceMaxInput}
              onChange={(e) => setPriceMaxInput(e.target.value)}
              placeholder="max"
              inputMode="numeric"
              disabled={subBlocked}
            />
          </div>

          <div className="drawerPriceBtns">
            <button
              type="button"
              className="btnOrange full"
              onClick={applyPrice}
              disabled={subBlocked}
            >
              Apply
            </button>

            <button
              type="button"
              className="btnGhost full"
              onClick={() => {
                clearPrice();
                setDrawerOpen(false);
              }}
              disabled={subBlocked}
            >
              Clear
            </button>
          </div>

          {priceActiveText ? <div className="activeFilter">{priceActiveText}</div> : null}
        </div>
      </div>
    </div>
  );

  return (
    <div className="jumia">
      <style>{css}</style>

      {/* ✅ OPTIONS POPUP */}
      {optOpen ? (
        <div className="optOverlay" onClick={() => closeOptionsPopup()} role="presentation">
          <div className="optModal" onClick={(e) => stopAll(e)} role="dialog" aria-modal="true">
            <div className="optHeader">
              <div>
                <div className="optHTitle">Choose options</div>
                <div className="optHSub">{safe(optProduct?.title) || "Item"}</div>
              </div>
              <button type="button" className="optX" onClick={() => closeOptionsPopup()}>
                ✕
              </button>
            </div>

            <div className="optBody">
              {popNeedsSize ? (
                <div className="optBlock">
                  <div className="optLabel">Size</div>
                  <div className="optChips">
                    {popSizes.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`optChip ${optSize === s ? "on" : ""}`}
                        onClick={() => setOptSize(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {popNeedsColor ? (
                <div className="optBlock">
                  <div className="optLabel">Color</div>
                  <div className="optChips">
                    {popColors.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`optChip ${optColor === c ? "on" : ""}`}
                        onClick={() => setOptColor(c)}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="optFooter">
              <button type="button" className="btnGhost" onClick={() => closeOptionsPopup()}>
                Cancel
              </button>
              <button
                type="button"
                className="btnOrange"
                onClick={() => {
                  const pid = String(optProduct?.id || "");
                  if (!pid) return;

                  if (popNeedsSize && !safe(optSize)) return;
                  if (popNeedsColor && !safe(optColor)) return;

                  addToCartFinal(pid, { size: optSize, color: optColor });
                  closeOptionsPopup();
                }}
                disabled={
                  subBlocked ||
                  !optProduct?.id ||
                  (popNeedsSize && !safe(optSize)) ||
                  (popNeedsColor && !safe(optColor))
                }
              >
                Add to cart
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ✅ MOBILE DRAWER */}
      {drawerOpen ? (
        <div className="drawerOverlay" onClick={() => setDrawerOpen(false)} role="presentation">
          {DrawerContent}
        </div>
      ) : null}

      {/* TOP STRIP */}
      <div className="topStrip">
        <div className="wrap topStripInner">
          <div className="tsLeft">
            <span className="tsChip">🔥 Deals</span>
            <span className="tsDot" />
            <span className="tsText">{safe(store?.locationText) || "Ghana"}</span>
          </div>

          <div className="tsRight">
            {user ? (
              <button type="button" className="acctBtn" onClick={goProfile}>
                <span className="acctAvatar">{profileText}</span>
                <span className="acctText">Account</span>
              </button>
            ) : (
              <button type="button" className="acctBtn" onClick={goSignup}>
                <span className="acctAvatar">👤</span>
                <span className="acctText">Sign in</span>
              </button>
            )}

            <button type="button" className="cartBtn" onClick={goCart}>
              🛒 <span className="cartTxt">Cart</span>
              {cartBadge > 0 ? <span className="cartBadge">{cartBadge}</span> : null}
            </button>
          </div>
        </div>
      </div>

      {/* HEADER (logo + search) */}
      <div className="header">
        <div className="wrap headerInner">
          <div className="brandRow">
            {/* ✅ REMOVED hamburger beside logo */}

            <div
              className="brand"
              role="button"
              tabIndex={0}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <div className="brandLogo">
                {safe(store?.logoUrl) ? (
                  <img src={store.logoUrl} alt={storeName} />
                ) : (
                  <div className="brandInit">{initials(storeName)}</div>
                )}
              </div>
              <div className="brandText">
                <div className="brandName">{storeName}</div>
                <div className="brandSub">{storeBio}</div>
              </div>
            </div>
          </div>

          <div className="searchBar">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products, brands and categories…"
              disabled={subBlocked}
            />
            <button
              type="button"
              className="searchGo"
              onClick={(e) => stopAll(e)}
              disabled={subBlocked}
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* HERO BANNER */}
      <div className="hero" style={{ backgroundImage: bannerBg }}>
        <div className="heroOverlay" />
        <div className="wrap heroInner">
          {loading ? (
            <div className="heroNotice">Loading store…</div>
          ) : msg ? (
            <div className={`heroNotice ${subBlocked ? "" : "err"}`}>{msg}</div>
          ) : (
            <div className="heroRow">
              <div className="heroTitle">Shop {storeName}</div>
              <div className="heroMeta">
                <span className="hmPill">✅ Verified store</span>
                <span className="hmPill">🛍️ Browse items</span>
                <span className="hmPill">📍 {safe(store?.locationText) || "Ghana"}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MAIN */}
      <div className="wrap main">
        {/* DESKTOP SIDEBAR ONLY */}
        <aside className="sidebar desktopOnly">
          <div className="sideCard">
            <div className="sideTitle">CATEGORY</div>
            <div className="sideList">
              {categories.slice(0, 16).map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`sideItem ${activeCat === c ? "on" : ""}`}
                  disabled={subBlocked}
                  onClick={(e) => {
                    stopAll(e);
                    if (subBlocked) return;
                    setActiveCat(c);
                  }}
                  title={c}
                >
                  <span className="sideIcon">{iconForCategory(c)}</span>
                  <span className="sideLabel">{c}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="sideCard">
            <div className="sideTitle">PRICE (GHS)</div>
            <div className="rangeRow">
              <input
                className="rangeIn"
                value={priceMinInput}
                onChange={(e) => setPriceMinInput(e.target.value)}
                placeholder="min"
                inputMode="numeric"
                disabled={subBlocked}
              />
              <span className="dash">—</span>
              <input
                className="rangeIn"
                value={priceMaxInput}
                onChange={(e) => setPriceMaxInput(e.target.value)}
                placeholder="max"
                inputMode="numeric"
                disabled={subBlocked}
              />
            </div>

            <div className="sidePriceBtns">
              <button type="button" className="btnOrange full" onClick={applyPrice} disabled={subBlocked}>
                Apply
              </button>
              <button type="button" className="btnGhost full" onClick={clearPrice} disabled={subBlocked}>
                Clear
              </button>
            </div>

            {priceActiveText ? <div className="activeFilter">{priceActiveText}</div> : null}
          </div>
        </aside>

        {/* CONTENT */}
        <section className="content">
          <div className="contentTop">
            <div>
              <div className="contentH1">
                Shop All Products
                <span className="count">{subBlocked ? "" : ` (${sorted.length})`}</span>
              </div>
              <div className="crumbs">
                Home <span className="crumbDot">›</span> {storeName}{" "}
                <span className="crumbDot">›</span> {activeCat}
              </div>

              {priceActiveText ? <div className="activeFilter inline">{priceActiveText}</div> : null}
            </div>

            <div className="sortRow">
              <label className="sortLabel">Sort by:</label>
              <select
                className="sortSel"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                disabled={subBlocked}
              >
                <option value="Newest">Newest</option>
                <option value="Discount">Biggest discount</option>
                <option value="PriceLow">Price: low to high</option>
                <option value="PriceHigh">Price: high to low</option>
              </select>
            </div>
          </div>

          {/* MOBILE QUICK BUTTONS */}
          <div className="mobileControls mobileOnly">
            <button
              type="button"
              className="btnGhost"
              onClick={() => setDrawerOpen(true)}
              disabled={subBlocked}
            >
              ☰ Categories & Filters
            </button>
            <button
              type="button"
              className="btnGhost"
              onClick={() => {
                clearPrice();
                setActiveCat("All");
                setSearch("");
              }}
              disabled={subBlocked}
            >
              Reset
            </button>
          </div>

          <div className="grid">
            {loading ? (
              <div className="empty">Loading…</div>
            ) : !store ? (
              <div className="empty err">Store not loaded.</div>
            ) : subBlocked ? (
              <div className="empty err">
                This store is temporarily inactive (seller not subscribed).
              </div>
            ) : sorted.length === 0 ? (
              <div className="empty">No products found.</div>
            ) : (
              sorted.map((p) => {
                const title = safe(p.title) || "Untitled";
                const img =
                  safe(p.thumbnailUrl) ||
                  safe(p.imageUrl) ||
                  safe(p?.mediaUrls?.[0]) ||
                  "";

                const cur = safe(p.currency) || "GHS";
                const priceVal = getPriceValue(p);

                const discount = Number(p.discountPercent || 0);
                const hasDiscount =
                  priceVal != null && Number.isFinite(discount) && discount > 0;

                const finalPriceVal = applyDiscount(priceVal, discount);

                const wished = !!wishlist?.[String(p.id)];
                const added = isInCart(p.id);

                return (
                  <div
                    className="card"
                    key={p.id}
                    onClick={() => goCard(p.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") goCard(p.id);
                    }}
                  >
                    <div className="imgBox">
                      {img ? (
                        <img src={img || "/placeholder.svg"} alt={title} />
                      ) : (
                        <div className="ph">📦</div>
                      )}

                      {hasDiscount ? <div className="badge">-{discount}%</div> : null}

                      <button
                        type="button"
                        className={`heart ${wished ? "on" : ""}`}
                        onClick={(e) => toggleWish(e, p)}
                        title={wished ? "Remove from wishlist" : "Save to wishlist"}
                      >
                        {wished ? "❤" : "♡"}
                      </button>
                    </div>

                    <div className="info">
                      <div className="title">{title}</div>

                      {priceVal == null ? (
                        <div className="price muted">Contact for price</div>
                      ) : hasDiscount ? (
                        <div className="priceStack">
                          <div className="newP">{money(finalPriceVal, cur)}</div>
                          <div className="oldP">{money(priceVal, cur)}</div>
                        </div>
                      ) : (
                        <div className="price">{money(priceVal, cur)}</div>
                      )}

                      <div className="actions">
                        <button
                          type="button"
                          className="btnOrange sm"
                          onClick={(e) => addToCart(e, p.id, p)}
                          disabled={subBlocked || added}
                          title={
                            added
                              ? "Already in cart"
                              : hasOptions(p)
                              ? "Choose options"
                              : "Add to cart"
                          }
                        >
                          {added ? "Added" : "Add"}
                        </button>

                        <button
                          type="button"
                          className="btnGhost sm"
                          onClick={(e) => goView(e, p.id)}
                          disabled={subBlocked}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

const css = `
:root{
  --bg:#f3f3f3;
  --paper:#fff;
  --ink:#111827;
  --muted:#6b7280;
  --line:#e5e7eb;
  --orange:#f68b1e;
  --orange2:#ffb200;
  --shadow:0 10px 24px rgba(17,24,39,.08);
}
*{ box-sizing:border-box; }
html,body{ width:100%; max-width:100%; overflow-x:hidden; }
.jumia{ min-height:100vh; background:var(--bg); color:var(--ink); font-family:Inter,system-ui,Segoe UI,Arial; }
.wrap{ width:min(1200px, 100%); margin:0 auto; padding:0 14px; }

/* Top strip */
.topStrip{ background:#fff; border-bottom:1px solid var(--line); }
.topStripInner{ height:44px; display:flex; align-items:center; justify-content:space-between; gap:10px; }
.tsLeft{ display:flex; align-items:center; gap:10px; min-width:0; }
.tsChip{ background:#fff7ed; border:1px solid #fed7aa; color:#9a3412; font-weight:900; padding:6px 10px; border-radius:999px; font-size:12px; white-space:nowrap; }
.tsDot{ width:4px; height:4px; background:#cbd5e1; border-radius:99px; }
.tsText{ color:#475569; font-weight:800; font-size:12px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.tsRight{ display:flex; align-items:center; gap:10px; }

.acctBtn{
  border:0; background:transparent; cursor:pointer;
  display:flex; align-items:center; gap:8px;
  padding:6px 10px; border-radius:10px; font-weight:900;
}
.acctBtn:hover{ background:#f8fafc; }
.acctAvatar{
  width:28px; height:28px; border-radius:999px;
  background:#111827; color:#fff; display:grid; place-items:center;
  font-weight:1000; font-size:12px;
}
.acctText{ font-size:13px; color:#111827; }

.cartBtn{
  position:relative;
  border:1px solid var(--line);
  background:#fff;
  border-radius:10px;
  padding:7px 10px;
  cursor:pointer;
  font-weight:1000;
  display:flex; align-items:center; gap:8px;
}
.cartTxt{ font-size:13px; }
.cartBadge{
  position:absolute; top:-7px; right:-7px;
  min-width:18px; height:18px; padding:0 6px;
  border-radius:999px; background:#ef4444; color:#fff;
  font-weight:1100; font-size:11px;
  display:grid; place-items:center; border:2px solid #fff;
}

/* Header */
.header{ background:#fff; border-bottom:1px solid var(--line); }
.headerInner{
  padding:12px 0;
  display:grid;
  grid-template-columns: 1fr 1.2fr;
  gap:14px;
  align-items:center;
}
.brandRow{ display:flex; align-items:center; gap:10px; min-width:0; }

/* ✅ hamburger removed entirely */
.hamburger{ display:none !important; }

.brand{ display:flex; align-items:center; gap:10px; cursor:pointer; min-width:0; }
.brandLogo{
  width:46px; height:46px; border-radius:12px; overflow:hidden;
  background:#f1f5f9; border:1px solid var(--line);
  display:grid; place-items:center;
}
.brandLogo img{ width:100%; height:100%; object-fit:cover; }
.brandInit{ font-weight:1100; color:#0f172a; }
.brandText{ min-width:0; }
.brandName{ font-weight:1100; font-size:16px; line-height:1.1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.brandSub{ color:var(--muted); font-weight:800; font-size:12px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

.searchBar{
  display:flex; align-items:center; overflow:hidden;
  border:2px solid #f1f5f9;
  border-radius:10px;
  background:#fff;
}
.searchBar input{
  flex:1; border:0; outline:none;
  padding:12px 12px;
  font-weight:800; font-size:14px;
}
.searchGo{
  border:0; cursor:pointer;
  background:var(--orange);
  color:#fff; font-weight:1100;
  padding:12px 16px;
}
.searchGo:disabled{ opacity:.65; cursor:not-allowed; }

/* Hero */
.hero{
  position:relative;
  min-height:120px;
  background-size:cover;
  background-position:center;
  background-repeat:no-repeat;
}
.heroOverlay{
  position:absolute; inset:0;
  background:linear-gradient(90deg, rgba(0,0,0,.55), rgba(0,0,0,.25));
}
.heroInner{ position:relative; padding:16px 0; }
.heroNotice{
  background:rgba(255,255,255,.95);
  border:1px solid var(--line);
  border-radius:12px;
  padding:12px 14px;
  box-shadow:var(--shadow);
  max-width:620px;
  font-weight:900;
}
.heroNotice.err{ border-color:#fecaca; color:#991b1b; }
.heroRow{ display:flex; align-items:flex-start; justify-content:space-between; gap:14px; flex-wrap:wrap; }
.heroTitle{ color:#fff; font-weight:1200; font-size:26px; letter-spacing:-.02em; }
.heroMeta{ display:flex; gap:10px; flex-wrap:wrap; }
.hmPill{
  background:rgba(255,255,255,.12);
  border:1px solid rgba(255,255,255,.22);
  color:#fff;
  font-weight:900;
  padding:7px 10px;
  border-radius:999px;
  font-size:12px;
}

/* Main layout */
.main{
  padding:14px 0 40px;
  display:grid;
  grid-template-columns: 280px 1fr;
  gap:14px;
}

/* Sidebar */
.sidebar{ position:sticky; top:10px; align-self:start; }
.sideCard{
  background:#fff;
  border:1px solid var(--line);
  border-radius:10px;
  box-shadow:0 2px 10px rgba(0,0,0,.03);
  padding:12px;
  margin-bottom:12px;
}
.sideTitle{ font-weight:1200; font-size:12px; color:#111827; letter-spacing:.04em; }
.sideList{ margin-top:10px; display:grid; gap:6px; }
.sideItem{
  border:0; background:transparent; cursor:pointer;
  display:flex; align-items:center; gap:10px;
  padding:9px 10px;
  border-radius:10px;
  font-weight:900;
  color:#111827;
  text-align:left;
}
.sideItem:hover{ background:#f8fafc; }
.sideItem.on{ background:#fff7ed; border:1px solid #fed7aa; }
.sideItem:disabled{ opacity:.6; cursor:not-allowed; }
.sideIcon{ width:20px; display:inline-grid; place-items:center; }
.sideLabel{ font-size:13px; }

.rangeRow{ display:flex; align-items:center; gap:8px; margin-top:10px; }
.rangeIn{
  width:100%;
  border:1px solid var(--line);
  border-radius:10px;
  padding:10px 10px;
  font-weight:900;
}
.dash{ color:#94a3b8; font-weight:1000; }

.sidePriceBtns, .drawerPriceBtns{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:8px;
  margin-top:10px;
}

.activeFilter{
  margin-top:10px;
  font-weight:900;
  font-size:12px;
  color:#0f172a;
  background:#f8fafc;
  border:1px solid #e2e8f0;
  border-radius:10px;
  padding:8px 10px;
}
.activeFilter.inline{ display:inline-block; margin-top:8px; }

/* Content header */
.contentTop{
  background:#fff;
  border:1px solid var(--line);
  border-radius:10px;
  padding:12px;
  display:flex;
  align-items:flex-end;
  justify-content:space-between;
  gap:12px;
  flex-wrap:wrap;
}
.contentH1{ font-weight:1200; font-size:18px; }
.count{ color:#64748b; font-weight:1000; margin-left:6px; }
.crumbs{ margin-top:4px; color:#64748b; font-weight:800; font-size:12px; }
.crumbDot{ color:#cbd5e1; padding:0 6px; }

.sortRow{ display:flex; align-items:center; gap:8px; }
.sortLabel{ font-weight:1000; color:#334155; font-size:12px; }
.sortSel{
  border:1px solid var(--line);
  border-radius:10px;
  padding:9px 10px;
  font-weight:1000;
  background:#fff;
}

/* Mobile controls */
.mobileControls{
  margin-top:10px;
  display:flex;
  gap:10px;
  flex-wrap:wrap;
}

/* Grid cards */
.grid{
  margin-top:12px;
  display:grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap:12px;
}
.card{
  background:#fff;
  border:1px solid var(--line);
  border-radius:10px;
  overflow:hidden;
  box-shadow:0 2px 10px rgba(0,0,0,.04);
  cursor:pointer;
  display:flex;
  flex-direction:column;
  outline:none;
}
.card:hover{ transform:translateY(-1px); box-shadow:0 10px 22px rgba(0,0,0,.08); transition:.15s; }

.imgBox{
  position:relative;
  width:100%;
  height:190px;
  background:#f1f5f9;
  overflow:hidden;
}
.imgBox img{ width:100%; height:100%; object-fit:cover; object-position:center; display:block; }
.ph{ height:100%; display:grid; place-items:center; font-size:42px; opacity:.75; }

.badge{
  position:absolute;
  left:10px; top:10px;
  background:var(--orange);
  color:#fff;
  font-weight:1200;
  font-size:12px;
  padding:6px 10px;
  border-radius:8px;
}
.heart{
  position:absolute;
  right:10px; top:10px;
  width:34px; height:34px;
  border-radius:999px;
  border:1px solid rgba(255,255,255,.7);
  background:rgba(255,255,255,.95);
  cursor:pointer;
  font-weight:1200;
  display:grid; place-items:center;
  color:#64748b;
}
.heart.on{ color:#ef4444; border-color:#fecaca; background:#fff1f2; }
.heart:active{ transform:scale(.98); }

.info{ padding:10px; display:flex; flex-direction:column; gap:6px; }
.title{
  font-weight:1000;
  font-size:13px;
  line-height:1.3;
  display:-webkit-box;
  -webkit-line-clamp:2;
  -webkit-box-orient:vertical;
  overflow:hidden;
  min-height:34px;
}
.price{ font-weight:1200; font-size:14px; }
.price.muted{ color:var(--muted); font-weight:1000; }
.priceStack{ display:flex; align-items:baseline; gap:10px; flex-wrap:wrap; }
.newP{ font-weight:1200; font-size:14px; }
.oldP{ color:#94a3b8; text-decoration:line-through; font-weight:1000; font-size:12px; }

.actions{ display:flex; gap:8px; margin-top:4px; }

.btnOrange{
  border:0;
  border-radius:10px;
  padding:10px 12px;
  background:linear-gradient(135deg, var(--orange), var(--orange2));
  color:#fff;
  font-weight:1200;
  cursor:pointer;
}
.btnOrange:disabled{ opacity:.6; cursor:not-allowed; }

.btnGhost{
  border:1px solid var(--line);
  border-radius:10px;
  padding:10px 12px;
  background:#fff;
  color:#111827;
  font-weight:1100;
  cursor:pointer;
}
.btnGhost:disabled{ opacity:.6; cursor:not-allowed; }

.sm{ padding:9px 10px; font-size:12px; flex:1; text-align:center; }
.full{ width:100%; }

/* Empty */
.empty{
  grid-column:1/-1;
  background:#fff;
  border:1px solid var(--line);
  border-radius:10px;
  padding:14px;
  font-weight:1000;
}
.empty.err{ border-color:#fecaca; color:#991b1b; }

/* Options modal */
.optOverlay{
  position:fixed; inset:0;
  background:rgba(2,6,23,.60);
  display:flex; align-items:center; justify-content:center;
  padding:16px;
  z-index:9999;
}
.optModal{
  width:min(520px, 100%);
  background:#fff;
  border-radius:12px;
  overflow:hidden;
  box-shadow:0 30px 90px rgba(0,0,0,.35);
  border:1px solid var(--line);
}
.optHeader{
  padding:12px 14px;
  display:flex; align-items:flex-start; justify-content:space-between; gap:10px;
  background:#fff7ed;
  border-bottom:1px solid #fed7aa;
}
.optHTitle{ font-weight:1200; font-size:14px; }
.optHSub{ margin-top:2px; color:#9a3412; font-weight:1000; font-size:12px; }
.optX{
  border:1px solid rgba(0,0,0,.08);
  background:#fff;
  border-radius:10px;
  padding:8px 10px;
  cursor:pointer;
  font-weight:1200;
}
.optBody{ padding:12px 14px; display:grid; gap:12px; }
.optLabel{ font-weight:1200; font-size:13px; }
.optChips{ margin-top:8px; display:flex; gap:8px; flex-wrap:wrap; }
.optChip{
  border:1px solid var(--line);
  background:#fff;
  border-radius:999px;
  padding:9px 12px;
  font-weight:1100;
  cursor:pointer;
}
.optChip.on{ border-color:#fdba74; background:#fff7ed; }
.optFooter{
  padding:12px 14px;
  display:flex;
  gap:10px;
  justify-content:flex-end;
  border-top:1px solid var(--line);
  background:#f8fafc;
}

/* Drawer */
.drawerOverlay{
  position:fixed;
  inset:0;
  background:rgba(2,6,23,.55);
  z-index:9998;
  display:flex;
  justify-content:flex-start;
}
.drawerPanel{
  width:min(360px, 88vw);
  height:100%;
  background:#fff;
  border-right:1px solid var(--line);
  box-shadow:0 30px 90px rgba(0,0,0,.25);
  display:flex;
  flex-direction:column;
}
.drawerHead{
  padding:12px 14px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  border-bottom:1px solid var(--line);
  background:#fff;
}
.drawerTitle{ font-weight:1200; }
.drawerX{
  border:1px solid var(--line);
  background:#fff;
  border-radius:10px;
  padding:8px 10px;
  cursor:pointer;
  font-weight:1200;
}
.drawerBody{
  padding:12px;
  overflow:auto;
}

/* Responsive */
@media (max-width:1100px){
  .grid{ grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width:920px){
  .main{ grid-template-columns: 1fr; }
  .grid{ grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .headerInner{ grid-template-columns: 1fr; }
}
@media (max-width:640px){
  .grid{ grid-template-columns: repeat(2, minmax(0, 1fr)); gap:10px; }
  .imgBox{ height:160px; }
  .acctText, .cartTxt{ display:none; }
}

/* helpers */
.desktopOnly{ display:block; }
.mobileOnly{ display:none; }
@media (max-width:920px){
  .desktopOnly{ display:none; }
  .mobileOnly{ display:flex; }
}
`;
